// shared/src/runs.ts
// Zod schemas + payload-summary helpers for the runs registry (migration 0022).
// Parallel to runtime-params.ts in structure: schemas at top, decoders below.

import { z } from 'zod';
import {
  RUN_SCHEDULERS,
  RUN_STATUSES,
  RUN_TRIGGERS,
  type RunRow,
  type RunStatus,
} from './types/runs.js';

// ─── Zod ────────────────────────────────────────────────────────────────────

export const RunSchedulerSchema = z.enum(
  RUN_SCHEDULERS as unknown as [string, ...string[]],
);
export const RunStatusSchema    = z.enum(
  RUN_STATUSES as unknown as [string, ...string[]],
);
export const RunTriggerSchema   = z.enum(
  RUN_TRIGGERS as unknown as [string, ...string[]],
);

/** Full row shape as it comes back from D1 (strings on the wire). */
export const RunRowSchema = z.object({
  id:               z.string().min(1),
  scheduler:        RunSchedulerSchema,
  method:           z.string().min(1),
  status:           RunStatusSchema,
  scheduled_at:     z.string().min(1),
  started_at:       z.string().nullable(),
  finished_at:      z.string().nullable(),
  duration_ms:      z.number().int().nonnegative().nullable(),
  trigger:          RunTriggerSchema,
  triggered_by:     z.string().nullable(),
  instance_id:      z.string().nullable(),
  payload_summary:  z.string().nullable(),
  error_message:    z.string().nullable(),
  parent_run_id:    z.string().nullable(),
  cancel_requested: z.union([z.literal(0), z.literal(1)]),
  params_hash:      z.string().nullable(),
  created_at:       z.string().min(1),
  // Read-derived from the briefs JOIN in /api/runs (CHAIN runs only). Queries
  // that don't join omit the column → .default(null) supplies it so .strict()
  // still accepts those rows. Per [[feedback_no_fallbacks]] this is a declared
  // absent-column default, not a silent error swallow.
  cost_usd:         z.number().nullable().default(null),
  // Sibling of cost_usd — briefs.run_shadow_cost_usd (per-brief shadow-A/B spend, migration
  // 0081, 2026-07-11). Same absent-column default contract per [[feedback_no_fallbacks]].
  shadow_cost_usd:  z.number().nullable().default(null),
}).strict();

/** Insert payload — created_at/status defaulted by writer; id required. */
export const RunInsertSchema = z.object({
  id:               z.string().min(1),
  scheduler:        RunSchedulerSchema,
  method:           z.string().min(1),
  status:           RunStatusSchema,
  scheduled_at:     z.string().min(1),
  started_at:       z.string().nullable(),
  trigger:          RunTriggerSchema,
  triggered_by:     z.string().nullable(),
  instance_id:      z.string().nullable(),
  parent_run_id:    z.string().nullable(),
  params_hash:      z.string().nullable(),
  created_at:       z.string().min(1),
}).strict();

/** Patch payload for status transitions — only the mutable columns. */
export const RunUpdateSchema = z.object({
  status:          RunStatusSchema.optional(),
  finished_at:     z.string().nullable().optional(),
  duration_ms:     z.number().int().nonnegative().nullable().optional(),
  payload_summary: z.string().nullable().optional(),
  error_message:   z.string().nullable().optional(),
  instance_id:     z.string().nullable().optional(),
  cancel_requested: z.union([z.literal(0), z.literal(1)]).optional(),
}).strict();

export type RunInsert = z.infer<typeof RunInsertSchema>;
export type RunUpdate = z.infer<typeof RunUpdateSchema>;

// ─── Payload-summary helpers ────────────────────────────────────────────────

/**
 * Truncate a JSON-serialized RPC return for `payload_summary` storage. Keeps
 * the row useful for drilldown without bloating D1 with multi-KB payloads.
 */
export function summarizePayload(value: unknown, maxChars: number = 2000): string | null {
  if (value === null || value === undefined) return null;
  let json: string;
  try {
    json = JSON.stringify(value);
  } catch (err) {
    // shared/ has lib:["ES2022"] (no DOM/Node/Workers types). Declare the
    // minimal console interface inline — console.warn exists in both Node
    // and the Workers runtime.
    const c = (globalThis as { console?: { warn(msg: string): void } }).console;
    c?.warn(JSON.stringify({ level: 'warn', kind: 'runs.summarizePayload.stringify_failed', error: String(err) }));
    return null;
  }
  if (json.length <= maxChars) return json;
  return json.slice(0, maxChars - 12) + '…truncated';
}

// ─── Row decoder (D1 → typed) ───────────────────────────────────────────────

/**
 * D1 returns SQLite types; this enforces the {0,1} INTEGER for cancel_requested
 * and rejects unknown columns. Throws on parse error per [[feedback_no_fallbacks]].
 */
export function parseRunRow(raw: unknown): RunRow {
  return RunRowSchema.parse(raw) as RunRow;
}

/**
 * For UI display: indicates whether a status row is mid-flight.
 */
export function isInFlight(status: RunStatus): boolean {
  return status === 'running';
}
